import { useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";

const RADIUS = 160;
const FORCE = 18;
const MAX_TY = 10;
const MOVE_TRANSITION = "transform 0.1s ease";
const RETURN_TRANSITION = "transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)";

function Word({ text, ice, refCallback, trailingSpace }) {
  const cls = ice ? "italic text-ice glow-ice font-normal" : "";
  return (
    <>
      <span ref={refCallback} style={{ display: "inline-block", position: "relative" }} className={cls}>
        {text}
      </span>
      {trailingSpace && " "}
    </>
  );
}

export default function Hero() {
  const noiseRef = useRef(null);
  const wordRefs = useRef([]);
  const { lang } = useLang();

  useEffect(() => {
    const el = noiseRef.current;
    if (!el) return;
    const canvas = document.createElement("canvas");
    canvas.width = 200; canvas.height = 200;
    const ctx = canvas.getContext("2d");
    const d = ctx.createImageData(200, 200);
    for (let i = 0; i < d.data.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      d.data[i] = d.data[i + 1] = d.data[i + 2] = v;
      d.data[i + 3] = 255;
    }
    ctx.putImageData(d, 0, 0);
    el.style.backgroundImage = `url(${canvas.toDataURL()})`;
  }, []);

  const handleMouseMove = useCallback((e) => {
    const mx = e.clientX, my = e.clientY;
    wordRefs.current.forEach((el) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = cx - mx, dy = cy - my;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < RADIUS && dist > 0) {
        const force = (1 - dist / RADIUS) * FORCE;
        const tx = (dx / dist) * force;
        const ty = Math.max(-MAX_TY, Math.min(MAX_TY, (dy / dist) * force));
        el.style.transition = MOVE_TRANSITION;
        el.style.transform = `translate(${tx}px, ${ty}px)`;
      } else {
        el.style.transition = RETURN_TRANSITION;
        el.style.transform = "";
      }
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    wordRefs.current.forEach((el) => {
      if (!el) return;
      el.style.transition = RETURN_TRANSITION;
      el.style.transform = "";
    });
  }, []);

  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{ height: "100dvh", minHeight: 560, maxHeight: "100dvh" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid="hero"
    >
      <video
        autoPlay muted loop playsInline
        poster="/og-image.jpg"
        style={{
          position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
          objectFit: "cover", zIndex: 0,
          transform: "translateZ(0)", willChange: "transform",
          backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
        }}
      >
        <source src="/przestrzen_web.mp4" type="video/mp4" />
      </video>

      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0, zIndex: 2,
          background: "linear-gradient(to bottom, rgba(0,0,0,0.52) 0%, rgba(0,0,0,0.36) 14%, rgba(0,0,0,0.16) 52%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      <div className="pointer-events-none absolute inset-0" style={{ zIndex: 4, background: "radial-gradient(ellipse at center, rgba(16,15,12,0) 0%, rgba(16,15,12,0.32) 100%)" }} />
      <div ref={noiseRef} className="pointer-events-none absolute inset-0" style={{ backgroundRepeat: "repeat", backgroundSize: "200px", opacity: 0.03, mixBlendMode: "overlay", zIndex: 5 }} aria-hidden="true" />

      <div className="relative z-10 text-center max-w-4xl mx-auto px-6 h-full flex flex-col items-center justify-center" style={{ paddingTop: "5.5rem", paddingBottom: "4.5rem" }}>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="mb-5 md:mb-7 flex items-center gap-4"
          data-testid="hero-overline"
        >
          <span className="inline-block w-8 h-px bg-amber/40" />
          <span className="font-body text-[11px] tracking-[0.32em] uppercase text-amber/80">
            {c.hero.overline[lang]}
          </span>
          <span className="inline-block w-8 h-px bg-amber/40" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-light leading-[1.12] tracking-tight text-white-warm"
          style={{ fontSize: "clamp(1.9rem, 4.2vw, 4.2rem)" }}
          data-testid="hero-title"
        >
          <Word text={c.hero.line1[lang]} ice={false} trailingSpace refCallback={(el) => { wordRefs.current[0] = el; }} />
          <br />
          <Word text={c.hero.ice[lang]} ice trailingSpace={false} refCallback={(el) => { wordRefs.current[1] = el; }} />
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.68, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 md:mt-5 font-mono text-[9px] md:text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-amber/75"
          data-testid="hero-keywords"
        >
          {c.hero.keywords[lang]}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="mt-3 md:mt-4 max-w-2xl mx-auto font-body text-sm md:text-base font-light leading-[1.75] md:leading-[1.85] text-white-dim"
          data-testid="hero-subtitle"
        >
          {c.hero.subtitle[lang]}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 md:mt-8 flex flex-wrap gap-3 md:gap-4 justify-center"
        >
          <a href="#kontakt" className="btn-primary" data-testid="hero-cta-contact">
            {c.hero.cta1[lang]}
          </a>
          <a href="#portfolio" className="btn-ghost" data-testid="hero-cta-portfolio">
            {c.hero.cta2[lang]}
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ zIndex: 10 }}
      >
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-white-dim">
          {c.hero.scroll[lang]}
        </span>
        <span
          className="block w-px h-12 animate-scroll-pulse"
          style={{ background: "linear-gradient(to bottom, rgba(124,196,216,0.9), rgba(124,196,216,0))" }}
        />
      </motion.div>
    </section>
  );
}